import type { AxisDomain } from 'recharts/types/util/types';

import type { FillPolicy } from './padSeriesToPeriod';

export type MetricChartConfig = {
  dataKey: string;
  label: string;
  domain: AxisDomain;
  valueFormatter: (value: number) => string;
  ticks?: number[];
  tickFormatter?: (value: number) => string;
  /**
   * How empty days are filled when padding the series to the selected period.
   * `zero` for counts (line dips to 0), `gap` for averages/rates (line breaks,
   * no fabricated value). `domain` is not a reliable discriminator, so this is
   * set explicitly per metric.
   */
  fill: FillPolicy;
};

export const CSAT_TICKS = [0, 1, 2, 3, 4, 5];
export const formatCsatTick = (value: number) => `${value}`;

const isFixedDomain = (domain: AxisDomain) =>
  Array.isArray(domain) && domain.every((bound) => typeof bound === 'number');

const clampToDomain = (domain: AxisDomain, value: number): number => {
  if (!isFixedDomain(domain)) {
    return value;
  }
  const [min, max] = domain as [number, number];
  return Math.min(max, Math.max(min, value));
};

export const clampSeriesToDomain = <T>(
  data: readonly T[],
  domain: AxisDomain,
  valueKey: string
): T[] =>
  data.map((point) => {
    const record = point as Record<string, unknown>;
    const value = record[valueKey];
    const comparisonValue = record.comparisonValue;
    return {
      ...point,
      // A gap day carries a null value; keep it null so the line breaks there.
      // Clamping null would coerce it to 0 and fabricate a data point.
      [valueKey]: value == null ? null : clampToDomain(domain, value as number),
      ...(comparisonValue != null
        ? { comparisonValue: clampToDomain(domain, comparisonValue as number) }
        : {}),
    } as T;
  });
